/**
 * @file components/certificates/CertChangesCard.tsx
 * @description 證書變更卡片（序93）。
 *
 * 流程：管理組填寫申請（ZHSR06-1 電子表單簡化版：變更類型＋變更內容/原因）→
 * 驗證組提供客戶簽名 → 上傳簽名申請書 → 證書變更評審（ZHSR06-2：通過/不通過＋意見，
 * 通過可關聯重新派案的新案件 id）→ 全部記錄保留為變更歷程。
 */

import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileDiff, Plus, Upload as UploadIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message, modal } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { FileLink } from '@/components/common/FileLink';
import { SectionCard } from '@/components/shell';

/** 變更申請項（GET /api/certificates/:id/changes） */
interface ChangeRequest {
  id: number;
  changeType: string;
  applicationData: { detail?: string; reason?: string } | null;
  signedFileId: number | null;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  reviewNote: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  linkedCaseId: number | null;
  createdAt: string;
  createdByName: string | null;
}

const STATUS_META: Record<string, { color: string; label: string }> = {
  draft: { color: 'default', label: '待客戶簽名' },
  submitted: { color: 'processing', label: '待評審' },
  approved: { color: 'success', label: '評審通過' },
  rejected: { color: 'error', label: '評審不通過' },
};

const CHANGE_TYPES = ['增列查驗', '減列查驗', '不定期查驗', '增加驗證場區', '基本資料變更', '其他'];

interface Props {
  certificateId: number;
  /** 是否可操作（certificate.issue 能力，admin/manager） */
  canManage: boolean;
}

export function CertChangesCard({ certificateId, canManage }: Props) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<{ changeType: string; detail?: string; reason?: string }>();
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ items: ChangeRequest[] }>({
    queryKey: ['certificate-changes', certificateId],
    queryFn: () => apiClient.get(`/api/certificates/${certificateId}/changes`),
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['certificate-changes', certificateId] });

  const create = useMutation({
    mutationFn: (v: { changeType: string; detail?: string; reason?: string }) =>
      apiClient.post(`/api/certificates/${certificateId}/changes`, {
        changeType: v.changeType,
        applicationData: { detail: v.detail, reason: v.reason },
      }),
    onSuccess: () => {
      message.success('變更申請已建立，請提供客戶簽名後上傳申請書');
      setCreateOpen(false);
      form.resetFields();
      invalidate();
    },
    onError: (e: Error) => {
      console.error('[CertChangesCard] create failed', e);
      message.error(e.message || '建立失敗');
    },
  });

  /** 上傳客戶簽名後的申請書掃描檔 */
  const handleUploadSigned = async (row: ChangeRequest, file: File) => {
    setUploadingId(row.id);
    try {
      const uploaded = await uploadFile(file, { scope: 'case' });
      await apiClient.post(`/api/certificates/${certificateId}/changes/${row.id}/signed`, {
        fileId: uploaded.id,
      });
      message.success('簽名申請書已上傳，進入變更評審');
      invalidate();
    } catch (e) {
      console.error('[CertChangesCard] upload signed failed', e);
      message.error(e instanceof Error ? e.message : '上傳失敗');
    } finally {
      setUploadingId(null);
    }
  };

  /** 變更評審（ZHSR06-2） */
  const openReview = (row: ChangeRequest) => {
    let decision: 'approved' | 'rejected' = 'approved';
    let note = '';
    let linkedCaseId: number | undefined;
    modal.confirm({
      title: `證書變更評審（ZHSR06-2）：${row.changeType}`,
      width: 520,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
          <Select<'approved' | 'rejected'>
            style={{ width: '100%' }}
            defaultValue="approved"
            onChange={(v) => {
              decision = v;
            }}
            options={[
              { value: 'approved', label: '評審通過' },
              { value: 'rejected', label: '評審不通過' },
            ]}
          />
          <Input.TextArea
            rows={3}
            maxLength={2000}
            placeholder="評審意見"
            onChange={(e) => {
              note = e.target.value;
            }}
          />
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              變更需重新派案時，填入新建案件 ID 以關聯（序93.2；可先於派案專區新建案件）：
            </Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 4 }}
              min={1}
              placeholder="關聯案件 ID（選填）"
              onChange={(v) => {
                linkedCaseId = v ?? undefined;
              }}
            />
          </div>
        </Space>
      ),
      okText: '送出評審',
      cancelText: '取消',
      onOk: async () => {
        await apiClient.post(`/api/certificates/${certificateId}/changes/${row.id}/review`, {
          decision,
          note: note.trim() || undefined,
          linkedCaseId,
        });
        message.success('評審已記錄');
        invalidate();
      },
    });
  };

  const columns: ColumnsType<ChangeRequest> = [
    { title: '變更類型', dataIndex: 'changeType', width: 120 },
    {
      title: '變更內容',
      key: 'detail',
      ellipsis: true,
      render: (_, r) =>
        [
          r.applicationData?.detail,
          r.applicationData?.reason && `原因：${r.applicationData.reason}`,
        ]
          .filter(Boolean)
          .join('；') || '—',
    },
    {
      title: '簽名申請書',
      dataIndex: 'signedFileId',
      width: 130,
      render: (v: number | null, r) =>
        v ? (
          <FileLink fileId={v} filename="申請書" compact />
        ) : canManage && r.status === 'draft' ? (
          <Upload
            showUploadList={false}
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => {
              void handleUploadSigned(r, file);
              return false; // 阻止 antd 內建 XHR，改走 OSS 直傳
            }}
          >
            <Button size="small" icon={<UploadIcon size={12} />} loading={uploadingId === r.id}>
              上傳簽名件
            </Button>
          </Upload>
        ) : (
          '—'
        ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label ?? v}</Tag>,
    },
    {
      title: '評審',
      key: 'review',
      width: 170,
      render: (_, r) => {
        if (r.reviewedAt) {
          return (
            <span style={{ fontSize: 12 }}>
              {r.reviewedByName ?? '—'}・{new Date(r.reviewedAt).toLocaleDateString('zh-TW')}
              {r.reviewNote ? `（${r.reviewNote}）` : ''}
              {r.linkedCaseId ? `・關聯案件 #${r.linkedCaseId}` : ''}
            </span>
          );
        }
        return canManage && r.status === 'submitted' ? (
          <Button size="small" type="primary" ghost onClick={() => openReview(r)}>
            變更評審
          </Button>
        ) : (
          '—'
        );
      },
    },
    {
      title: '申請時間',
      dataIndex: 'createdAt',
      width: 120,
      render: (v: string, r) => (
        <span style={{ fontSize: 12 }}>
          {new Date(v).toLocaleDateString('zh-TW')}
          {r.createdByName ? `・${r.createdByName}` : ''}
        </span>
      ),
    },
  ];

  return (
    <SectionCard
      icon={<FileDiff size={14} />}
      title={`證書變更歷程（${data?.items.length ?? 0} 筆）`}
      padless
      actions={
        canManage && (
          <Button
            type="primary"
            size="small"
            icon={<Plus size={12} />}
            onClick={() => setCreateOpen(true)}
          >
            申請變更
          </Button>
        )
      }
    >
      <Table<ChangeRequest>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: '尚無變更記錄' }}
      />

      {/* 申請變更 Modal（ZHSR06-1 簡化電子表單） */}
      <Modal
        title="證書變更申請（ZHSR06-1）"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        okText="建立申請"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Form.Item
            name="changeType"
            label="變更類型"
            rules={[{ required: true, message: '請選擇變更類型' }]}
          >
            <Select options={CHANGE_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="detail" label="變更內容">
            <Input.TextArea rows={3} maxLength={2000} placeholder="變更明細（欄位/範圍/場區…）" />
          </Form.Item>
          <Form.Item name="reason" label="變更原因">
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            建立後由驗證組提供客戶簽名，簽名申請書上傳後進入變更評審（ZHSR06-2）；
            需重新派案的變更請於派案專區另建案件並在評審時關聯。
          </Typography.Text>
        </Form>
      </Modal>
    </SectionCard>
  );
}
